import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { api, onForcedLogout, tokens } from './api'
import type { User } from './types'

interface AuthState {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<User>
  register: (input: RegisterInput) => Promise<User>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  setUser: (user: User) => void
}

export interface RegisterInput {
  email: string
  password: string
  fullName?: string
  company?: string
  acceptTerms: boolean
}

interface TokenResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  user?: User
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  // Start "loading" only when there is a token worth validating; otherwise the
  // public pages render immediately with no auth round-trip.
  const [loading, setLoading] = useState(() => Boolean(tokens.access))

  const refreshUser = useCallback(async () => {
    if (!tokens.access) {
      setUser(null)
      setLoading(false)
      return
    }
    try {
      setUser(await api.get<User>('/auth/me'))
    } catch {
      tokens.clear()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshUser()
  }, [refreshUser])

  // The API client shouts when a refresh fails; drop the session in response.
  useEffect(() => onForcedLogout(() => setUser(null)), [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<TokenResponse>('/auth/login', { email, password }, { auth: false })
    tokens.set(res.accessToken, res.refreshToken)
    const nextUser = res.user ?? (await api.get<User>('/auth/me'))
    setUser(nextUser)
    return nextUser
  }, [])

  const register = useCallback(async (input: RegisterInput) => {
    const res = await api.post<TokenResponse>('/auth/register', input, { auth: false })
    tokens.set(res.accessToken, res.refreshToken)
    const nextUser = res.user ?? (await api.get<User>('/auth/me'))
    setUser(nextUser)
    return nextUser
  }, [])

  const logout = useCallback(async () => {
    const refresh = tokens.refresh
    if (refresh) {
      // Best-effort revoke; the local session goes away regardless.
      await api.post('/auth/logout', { refreshToken: refresh }).catch(() => undefined)
    }
    tokens.clear()
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, register, logout, refreshUser, setUser }),
    [user, loading, login, register, logout, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
